import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Table, Card, Tag, Space, Button, Input, Select, DatePicker, Statistic, Row, Col, message, Spin 
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fetchInvoices, fetchPaymentStats, Invoice } from '../lib/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status_filter: undefined as string | undefined,
    type_filter: undefined as string | undefined,
    limit: 50,
    skip: 0,
  });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  // Fetch invoices
  const { data: invoicesData, isLoading, refetch } = useQuery({
    queryKey: ['invoices', filters, dateRange],
    queryFn: () => fetchInvoices({
      ...filters,
      start_date: dateRange[0]?.format('YYYY-MM-DD'),
      end_date: dateRange[1]?.format('YYYY-MM-DD'),
    }),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['payment-stats', dateRange],
    queryFn: () => fetchPaymentStats({
      start_date: dateRange[0]?.format('YYYY-MM-DD'),
      end_date: dateRange[1]?.format('YYYY-MM-DD'),
    }),
  });

  const columns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text: string, record: Invoice) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/invoices/${record.id}`)}
          icon={<FileTextOutlined />}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors = {
          open: 'blue',
          pending: 'orange',
          paid: 'green',
          failed: 'red',
          expired: 'gray',
        };
        const icons = {
          open: <ClockCircleOutlined />,
          pending: <ClockCircleOutlined />,
          paid: <CheckCircleOutlined />,
          failed: <CloseCircleOutlined />,
          expired: <CloseCircleOutlined />,
        };
        return (
          <Tag color={colors[status as keyof typeof colors]} icon={icons[status as keyof typeof icons]}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="purple">{type.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: Invoice) => (
        <Space direction="vertical" size={0}>
          <span><strong>{record.total_amount.toFixed(2)} {record.currency}</strong></span>
          {record.zar_amount && <span style={{ fontSize: '12px', color: '#888' }}>{record.zar_amount} ZAR</span>}
        </Space>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_: any, record: Invoice) => (
        <Space direction="vertical" size={0}>
          <span>{record.customer_name || 'N/A'}</span>
          <span style={{ fontSize: '12px', color: '#888' }}>{record.customer_email}</span>
        </Space>
      ),
    },
    {
      title: 'Payments',
      dataIndex: 'payment_count',
      key: 'payment_count',
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>{count || 0}</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Invoice) => (
        <Button 
          type="primary" 
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/invoices/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <h1>Invoice Management</h1>

      {/* Stats Cards */}
      {statsData && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Invoices"
                value={statsData.invoices.total}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Paid Invoices"
                value={statsData.invoices.paid}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pending"
                value={statsData.invoices.pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Revenue (USD)"
                value={statsData.revenue.total_usd}
                precision={2}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            allowClear
            value={filters.status_filter}
            onChange={(value) => setFilters({ ...filters, status_filter: value, skip: 0 })}
            options={[
              { label: 'Open', value: 'open' },
              { label: 'Pending', value: 'pending' },
              { label: 'Paid', value: 'paid' },
              { label: 'Failed', value: 'failed' },
              { label: 'Expired', value: 'expired' },
            ]}
          />
          <Select
            placeholder="Type"
            style={{ width: 180 }}
            allowClear
            value={filters.type_filter}
            onChange={(value) => setFilters({ ...filters, type_filter: value, skip: 0 })}
            options={[
              { label: 'Subscription', value: 'subscription' },
              { label: 'Wallet Top-up', value: 'wallet_topup' },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={invoicesData?.invoices || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: invoicesData?.total || 0,
            pageSize: filters.limit,
            current: Math.floor(filters.skip / filters.limit) + 1,
            onChange: (page, pageSize) => {
              setFilters({
                ...filters,
                skip: (page - 1) * pageSize,
                limit: pageSize,
              });
            },
            showTotal: (total) => `Total ${total} invoices`,
          }}
        />
      </Card>
    </div>
  );
};

export default Invoices;

